import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getLink,
    getPageLinkByNodeId,
    LinkModel,
    mapLinks,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "@adobe/css-tools";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: LinkListStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const titleLink = getLink(state, ownProps.fields.titleLink);
    const { session } = state.context;
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.links,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );
    return {
        titleLink,
        links,
        session,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}
.title-wrapper {
}
.list-title {
}
.links-wrapper {
}
.list-link {
}
`;
export const LinkList: React.FC<Props> = ({ fields, titleLink, links, extendedStyles, session }) => {
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    const { hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    if (hasMissingCategories) {
        return null;
    }
    // console.log("isAuthenticated", isAuthenticated);
    const alignmentStyles = {
        css: css`
            text-align: ${fields.alignment || "right"};
            ${styles.linkListWrapper?.css}
        `,
    };

    const addToDataLayer = (title: string | undefined) => {
        const dataLayer = (window as any).dataLayer || [];
        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "footer_interaction",
                ecommerce: {
                    footer_text: title,
                },
            });
        }
        return dataLayer;
    };

    const directionStyles = {
        css: css`
            ${fields.direction === "horizontal"
                ? `
            display: inline-block;
            padding: 1px 18px 0 0;
            &:last-child {
                padding: 1px 0 0 0;
            }
        `
                : "padding: 1px 0 0 0;"}
            ${styles.linkWrapper?.css}
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {titleLink?.url ? (
                        <Link href={titleLink.url}>{fields.title || titleLink.title}</Link>
                    ) : (
                        <Typography as="h3" {...styles.title}>
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
                {links.map((link, index) => (
                    <StyledWrapper className="links-wrapper" key={index} {...directionStyles}>
                        {link?.url &&
                            (link.url === "/myaccount/create-account" ? (
                                isAuthenticated ? (
                                    ""
                                ) : (
                                    <Link
                                        href={link.url}
                                        className="list-link"
                                        onClick={() =>
                                            addToDataLayer(fields.links[index].fields.overriddenTitle || link.title)
                                        }
                                        target={link.openInNewWindow ? "_blank" : ""}
                                    >
                                        {link.overriddenTitle || link.title}
                                    </Link>
                                )
                            ) : (
                                <Link
                                    href={link.url}
                                    className="list-link"
                                    onClick={() =>
                                        addToDataLayer(fields.links[index].fields.overriddenTitle || link.title)
                                    }
                                    target={link.openInNewWindow ? "_blank" : ""}
                                >
                                    {link.overriddenTitle || link.title}
                                </Link>
                            ))}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(LinkList),
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const { categories } = (state as any).pageEditor;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
